/**
 * Brand Wheel Carousel - 3D Perspective Wheel with Alphabet Navigation
 *
 * Features:
 * - 3D perspective wheel rotation using CSS transforms
 * - 7 visible brands (center featured + 3 on each side)
 * - Alphabet navigation bar with letter filtering
 * - Click behavior: first click centers, second click follows link
 * - Touch/drag support for mobile
 * - Keyboard navigation (arrow keys)
 * - Auto-rotation with "zip" animation to random letters
 * - Respects prefers-reduced-motion
 */

if (!customElements.get('brand-wheel-carousel')) {
  class BrandWheelCarousel extends HTMLElement {
    constructor() {
      super();

      // DOM References
      this.track = this.querySelector('.brand-wheel__track');
      this.items = Array.from(this.querySelectorAll('.brand-wheel__item'));
      this.prevButton = this.querySelector('.brand-wheel__arrow--prev');
      this.nextButton = this.querySelector('.brand-wheel__arrow--next');
      this.alphabetButtons = this.querySelectorAll('.brand-wheel__alphabet-btn');
      this.dots = this.querySelectorAll('.brand-wheel__dot');
      this.counterCurrent = this.querySelector('.brand-wheel__counter-current');
      this.counterTotal = this.querySelector('.brand-wheel__counter-total');
      this.infoPanel = this.querySelector('.brand-wheel__info-panel');
      this.infoName = this.querySelector('.brand-wheel__info-name');
      this.infoTagline = this.querySelector('.brand-wheel__info-tagline');

      // Configuration from data attributes
      this.autoRotate = this.dataset.autoRotate === 'true';
      this.pauseDuration = (parseInt(this.dataset.pauseDuration, 10) || 10) * 1000;
      this.inactivityResume = (parseInt(this.dataset.inactivityResume, 10) || 5) * 1000;

      // State
      this.currentIndex = 0;
      this.totalItems = this.items.length;
      this.isAnimating = false;
      this.selectedItem = null;
      this.autoRotateTimeout = null;
      this.inactivityTimeout = null;
      this.userInteracted = false;
      this.isInViewport = false;

      // Reduced motion preference
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      // Touch/drag state
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragCurrentX = 0;
      this.dragStartRotation = 0;
      this.dragThreshold = 40;

      // Angle calculation
      this.angleStep = this.totalItems > 0 ? 360 / this.totalItems : 0;

      // Bind methods
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleMouseUp = this.handleMouseUp.bind(this);
    }

    connectedCallback() {
      if (this.items.length > 0) {
        this.init();
      }
    }

    init() {
      // Sort items alphabetically by brand name
      this.sortItemsAlphabetically();
      this.positionItems();
      this.bindEvents();
      this.updateVisibility();
      this.updateAlphabetHighlight();
      this.updateCounter();
      this.updateDots();
      this.initViewportObserver();

      // Listen for reduced motion changes
      this.reducedMotion.addEventListener('change', () => this.handleReducedMotionChange());
    }

    /**
     * Sort items alphabetically and reorder them in the DOM
     */
    sortItemsAlphabetically() {
      // Create array with brand names for sorting
      const itemsWithNames = this.items.map((item) => ({
        element: item,
        name: (item.dataset.brandName || '').toLowerCase()
      }));

      // Sort alphabetically
      itemsWithNames.sort((a, b) => a.name.localeCompare(b.name));

      // Reorder in DOM and update items array, update data-index attributes
      itemsWithNames.forEach((item, newIndex) => {
        this.track.appendChild(item.element);
        item.element.dataset.index = newIndex;
      });

      // Update items array to reflect new order
      this.items = itemsWithNames.map((item) => item.element);
      this.totalItems = this.items.length;
      this.angleStep = this.totalItems > 0 ? 360 / this.totalItems : 0;
    }

    /**
     * Position items around the 3D wheel cylinder
     */
    positionItems() {
      const radius = parseInt(getComputedStyle(this).getPropertyValue('--wheel-radius')) || 350;

      this.items.forEach((item, index) => {
        const angle = this.angleStep * index;
        item.style.transform = `rotateY(${angle}deg) translateZ(${radius}px)`;
        item.dataset.angle = angle;
      });
    }

    /**
     * Bind all event listeners
     */
    bindEvents() {
      // Arrow buttons
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.navigate(-1));
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.navigate(1));
      }

      // Keyboard navigation
      this.addEventListener('keydown', (e) => this.handleKeydown(e));

      // Item clicks - use data-index attribute for correct index after sorting
      this.items.forEach((item) => {
        item.addEventListener('click', (e) => {
          const index = parseInt(item.dataset.index, 10);
          this.handleItemClick(e, index);
        });
      });

      // Alphabet navigation - all letters are clickable
      this.alphabetButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.navigateToLetter(btn.dataset.letter));
      });

      // Dot indicators
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.userInteracted = true;
          this.pauseAutoRotation();
          this.resetInactivityTimer();
          this.goToIndex(index);
        });
      });

      // Touch events
      this.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
      this.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
      this.addEventListener('touchend', (e) => this.handleTouchEnd(e));

      // Mouse drag events
      this.addEventListener('mousedown', (e) => this.handleMouseDown(e));

      // Pause auto-rotation on hover/focus
      this.addEventListener('mouseenter', () => this.pauseAutoRotation());
      this.addEventListener('mouseleave', () => this.handleMouseLeave());
      this.addEventListener('focusin', () => this.pauseAutoRotation());
      this.addEventListener('focusout', (e) => this.handleFocusOut(e));
    }

    /**
     * Navigate by direction (-1 or +1)
     */
    navigate(direction) {
      if (this.isAnimating) return;

      this.userInteracted = true;
      this.pauseAutoRotation();
      this.resetInactivityTimer();

      const newIndex = (this.currentIndex + direction + this.totalItems) % this.totalItems;
      this.goToIndex(newIndex);
    }

    /**
     * Go to a specific index with optional zip mode
     */
    goToIndex(index, isZipping = false) {
      if (this.isAnimating && !isZipping) return;

      this.isAnimating = true;
      this.currentIndex = index;

      // Calculate rotation angle for the track
      const rotation = -this.angleStep * index;

      // Apply zipping class for fast animation
      if (isZipping) {
        this.track.classList.add('brand-wheel__track--zipping');
      } else {
        this.track.classList.remove('brand-wheel__track--zipping');
      }

      this.track.style.transform = `rotateY(${rotation}deg)`;

      // Update UI elements
      this.updateVisibility();
      this.updateAlphabetHighlight();
      this.updateCounter();
      this.updateDots();

      // Clear selection when navigating (unless zipping)
      if (!isZipping) {
        this.clearSelection();
      }

      // Reset animation lock after transition
      const duration = isZipping ? 80 : 500;
      setTimeout(() => {
        this.isAnimating = false;
        this.track.classList.remove('brand-wheel__track--zipping');
      }, duration);
    }

    /**
     * Navigate to the brand nearest to the given letter
     * If exact match exists, go there. Otherwise find the closest brand alphabetically.
     */
    navigateToLetter(letter) {
      this.userInteracted = true;
      this.pauseAutoRotation();
      this.resetInactivityTimer();

      // First try to find exact matches for this letter
      const exactMatches = this.items
        .map((item, index) => ({ item, index, name: item.dataset.brandName || '' }))
        .filter((entry) => entry.item.dataset.letter === letter)
        .sort((a, b) => a.name.localeCompare(b.name));

      if (exactMatches.length > 0) {
        this.goToIndex(exactMatches[0].index);
        return;
      }

      // No exact match - find the nearest brand alphabetically
      // Since items are sorted alphabetically, find the first brand that comes after this letter
      const letterLower = letter.toLowerCase();

      // Find the first brand that starts with a letter >= the clicked letter
      let nearestIndex = -1;
      for (let i = 0; i < this.items.length; i++) {
        const brandName = (this.items[i].dataset.brandName || '').toLowerCase();
        const brandFirstLetter = brandName.charAt(0);

        if (brandFirstLetter >= letterLower) {
          nearestIndex = i;
          break;
        }
      }

      // If no brand found after this letter, wrap to the first brand
      if (nearestIndex === -1) {
        nearestIndex = 0;
      }

      this.goToIndex(nearestIndex);
    }

    /**
     * Handle item click - two-click behavior
     */
    handleItemClick(e, index) {
      // Don't handle if we were dragging
      if (this.wasDragging) {
        this.wasDragging = false;
        return;
      }

      e.preventDefault();
      this.userInteracted = true;
      this.pauseAutoRotation();
      this.resetInactivityTimer();

      const item = this.items[index];

      if (index === this.currentIndex && this.selectedItem === item) {
        // Second click on selected centered item - follow link
        const link = item.dataset.link;
        if (link && link.length > 0) {
          window.location.href = link;
        }
      } else if (index === this.currentIndex) {
        // First click on centered item - show info panel
        this.selectItem(item);
      } else {
        // Click on non-centered item - center it
        this.goToIndex(index);
        // Select after animation completes
        setTimeout(() => this.selectItem(item), 520);
      }
    }

    /**
     * Select an item and show info panel
     */
    selectItem(item) {
      this.selectedItem = item;

      const name = item.dataset.brandName || '';
      const tagline = item.dataset.tagline || '';

      if (this.infoName) this.infoName.textContent = name;
      if (this.infoTagline) this.infoTagline.textContent = tagline;

      if (this.infoPanel) {
        this.infoPanel.classList.add('brand-wheel__info-panel--visible');
        this.infoPanel.setAttribute('aria-hidden', 'false');
      }
    }

    /**
     * Clear selection and hide info panel
     */
    clearSelection() {
      this.selectedItem = null;

      if (this.infoPanel) {
        this.infoPanel.classList.remove('brand-wheel__info-panel--visible');
        this.infoPanel.setAttribute('aria-hidden', 'true');
      }
    }

    /**
     * Update visibility classes based on distance from center
     */
    updateVisibility() {
      const visibleRange = 3;

      this.items.forEach((item, index) => {
        // Calculate shortest distance around the wheel
        let distance = Math.abs(index - this.currentIndex);
        if (distance > this.totalItems / 2) {
          distance = this.totalItems - distance;
        }

        // Remove all state classes
        item.classList.remove(
          'brand-wheel__item--featured',
          'brand-wheel__item--adjacent',
          'brand-wheel__item--far',
          'brand-wheel__item--hidden'
        );

        // Apply appropriate class based on distance
        if (distance === 0) {
          item.classList.add('brand-wheel__item--featured');
          item.setAttribute('aria-current', 'true');
        } else {
          item.removeAttribute('aria-current');
          if (distance === 1) {
            item.classList.add('brand-wheel__item--adjacent');
          } else if (distance <= visibleRange) {
            item.classList.add('brand-wheel__item--far');
          } else {
            item.classList.add('brand-wheel__item--hidden');
          }
        }
      });
    }

    /**
     * Update alphabet bar highlighting
     */
    updateAlphabetHighlight() {
      const currentItem = this.items[this.currentIndex];
      const currentLetter = currentItem?.dataset.letter || '';

      this.alphabetButtons.forEach((btn) => {
        const isActive = btn.dataset.letter === currentLetter;
        btn.classList.toggle('brand-wheel__alphabet-btn--active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    /**
     * Update counter display
     */
    updateCounter() {
      if (this.counterCurrent) {
        this.counterCurrent.textContent = this.currentIndex + 1;
      }
    }

    /**
     * Update dot indicators
     */
    updateDots() {
      this.dots.forEach((dot, index) => {
        const isActive = index === this.currentIndex;
        dot.classList.toggle('brand-wheel__dot--active', isActive);
        dot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    /**
     * Keyboard navigation
     */
    handleKeydown(e) {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.navigate(-1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.navigate(1);
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (document.activeElement?.classList.contains('brand-wheel__item')) {
            const index = this.items.indexOf(document.activeElement);
            if (index !== -1) {
              this.handleItemClick(e, index);
            }
          }
          break;
      }
    }

    // =========================================================================
    // Touch/Drag Handlers
    // =========================================================================

    handleTouchStart(e) {
      if (this.isAnimating) return;

      this.isDragging = true;
      this.wasDragging = false;
      this.dragStartX = e.touches[0].clientX;
      this.dragCurrentX = this.dragStartX;
      this.dragStartRotation = -this.angleStep * this.currentIndex;

      // Store the touched element for tap detection
      this.touchStartTarget = e.target.closest('.brand-wheel__item');

      this.classList.add('brand-wheel--dragging');
      this.pauseAutoRotation();
    }

    handleTouchMove(e) {
      if (!this.isDragging) return;

      this.dragCurrentX = e.touches[0].clientX;
      const diff = this.dragCurrentX - this.dragStartX;

      // Only prevent default if we're moving horizontally
      if (Math.abs(diff) > 10) {
        e.preventDefault();
        this.wasDragging = true;
        this.touchStartTarget = null; // Clear tap target if dragging

        // Calculate rotation based on drag distance
        const sensitivity = 0.3;
        const dragRotation = diff * sensitivity;
        const newRotation = this.dragStartRotation + dragRotation;

        this.track.style.transform = `rotateY(${newRotation}deg)`;
      }
    }

    handleTouchEnd(e) {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.classList.remove('brand-wheel--dragging');

      const diff = this.dragCurrentX - this.dragStartX;

      if (Math.abs(diff) > this.dragThreshold) {
        // Navigate based on drag direction
        const direction = diff > 0 ? -1 : 1;
        const steps = Math.max(1, Math.floor(Math.abs(diff) / 80));

        for (let i = 0; i < steps; i++) {
          setTimeout(() => {
            this.currentIndex = (this.currentIndex + direction + this.totalItems) % this.totalItems;
            if (i === steps - 1) {
              this.goToIndex(this.currentIndex);
            }
          }, i * 50);
        }
      } else if (this.touchStartTarget && !this.wasDragging) {
        // This was a tap on an item - handle it
        const tappedItem = this.touchStartTarget;
        const itemIndex = parseInt(tappedItem.dataset.index, 10);

        if (!isNaN(itemIndex) && itemIndex !== this.currentIndex) {
          // Tapped on a non-centered item - center it
          e.preventDefault();
          this.userInteracted = true;
          this.pauseAutoRotation();
          this.resetInactivityTimer();
          this.goToIndex(itemIndex);
          setTimeout(() => this.selectItem(tappedItem), 520);
        } else if (itemIndex === this.currentIndex) {
          // Tapped on centered item - handle selection/link
          this.userInteracted = true;
          this.pauseAutoRotation();
          this.resetInactivityTimer();

          if (this.selectedItem === tappedItem) {
            // Second tap - follow link
            const link = tappedItem.dataset.link;
            if (link && link.length > 0) {
              window.location.href = link;
            }
          } else {
            // First tap - select and show info
            this.selectItem(tappedItem);
          }
        }
      } else {
        // Snap back to current position
        this.goToIndex(this.currentIndex);
      }

      this.touchStartTarget = null;
      this.userInteracted = true;
      this.resetInactivityTimer();
    }

    handleMouseDown(e) {
      // Only respond to left click
      if (e.button !== 0) return;
      if (this.isAnimating) return;

      // Don't start drag on buttons
      if (e.target.closest('button')) return;

      this.isDragging = true;
      this.wasDragging = false;
      this.dragStartX = e.clientX;
      this.dragCurrentX = this.dragStartX;
      this.dragStartRotation = -this.angleStep * this.currentIndex;

      this.classList.add('brand-wheel--dragging');
      this.pauseAutoRotation();

      // Add document-level listeners
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);
    }

    handleMouseMove(e) {
      if (!this.isDragging) return;

      this.dragCurrentX = e.clientX;
      const diff = this.dragCurrentX - this.dragStartX;

      if (Math.abs(diff) > 5) {
        this.wasDragging = true;

        const sensitivity = 0.3;
        const dragRotation = diff * sensitivity;
        const newRotation = this.dragStartRotation + dragRotation;

        this.track.style.transform = `rotateY(${newRotation}deg)`;
      }
    }

    handleMouseUp() {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.classList.remove('brand-wheel--dragging');

      // Remove document-level listeners
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);

      const diff = this.dragCurrentX - this.dragStartX;

      if (Math.abs(diff) > this.dragThreshold) {
        const direction = diff > 0 ? -1 : 1;
        const steps = Math.max(1, Math.floor(Math.abs(diff) / 80));

        let newIndex = this.currentIndex;
        for (let i = 0; i < steps; i++) {
          newIndex = (newIndex + direction + this.totalItems) % this.totalItems;
        }
        this.goToIndex(newIndex);
      } else {
        this.goToIndex(this.currentIndex);
      }

      this.userInteracted = true;
      this.resetInactivityTimer();
    }

    // =========================================================================
    // Auto-rotation Logic
    // =========================================================================

    /**
     * Start auto-rotation cycle
     */
    startAutoRotation() {
      if (this.reducedMotion.matches || !this.isInViewport) return;

      this.scheduleNextZip();
    }

    /**
     * Schedule the next zip animation
     */
    scheduleNextZip() {
      if (this.autoRotateTimeout) {
        clearTimeout(this.autoRotateTimeout);
      }

      this.autoRotateTimeout = setTimeout(() => {
        if (!this.userInteracted || !this.isInViewport) {
          this.zipToRandomLetter();
        }
      }, this.pauseDuration);
    }

    /**
     * Zip to a random letter that has brands
     */
    zipToRandomLetter() {
      if (this.isAnimating || !this.isInViewport) return;

      // Get unique letters that have brands
      const availableLetters = [...new Set(this.items.map((item) => item.dataset.letter))];

      if (availableLetters.length <= 1) {
        this.scheduleNextZip();
        return;
      }

      // Pick a random letter different from current
      const currentLetter = this.items[this.currentIndex]?.dataset.letter;
      const otherLetters = availableLetters.filter((l) => l !== currentLetter);

      if (otherLetters.length === 0) {
        this.scheduleNextZip();
        return;
      }

      const targetLetter = otherLetters[Math.floor(Math.random() * otherLetters.length)];

      // Find first brand with this letter (alphabetically sorted)
      const matchingItems = this.items
        .map((item, index) => ({ item, index, name: item.dataset.brandName || '' }))
        .filter((entry) => entry.item.dataset.letter === targetLetter)
        .sort((a, b) => a.name.localeCompare(b.name));

      if (matchingItems.length > 0) {
        this.performZipAnimation(matchingItems[0].index);
      }
    }

    /**
     * Perform rapid "zip" animation through positions
     */
    performZipAnimation(targetIndex) {
      // Calculate shortest path
      let forwardSteps = (targetIndex - this.currentIndex + this.totalItems) % this.totalItems;
      let backwardSteps = (this.currentIndex - targetIndex + this.totalItems) % this.totalItems;

      const direction = forwardSteps <= backwardSteps ? 1 : -1;
      const steps = Math.min(forwardSteps, backwardSteps);

      if (steps === 0) {
        this.scheduleNextZip();
        return;
      }

      let step = 0;
      const zipInterval = setInterval(() => {
        step++;
        const nextIndex = (this.currentIndex + direction + this.totalItems) % this.totalItems;
        this.goToIndex(nextIndex, true);

        if (step >= steps) {
          clearInterval(zipInterval);
          // Final update without zip mode
          setTimeout(() => {
            this.updateVisibility();
            this.scheduleNextZip();
          }, 100);
        }
      }, 80);
    }

    /**
     * Pause auto-rotation
     */
    pauseAutoRotation() {
      if (this.autoRotateTimeout) {
        clearTimeout(this.autoRotateTimeout);
        this.autoRotateTimeout = null;
      }
    }

    /**
     * Resume auto-rotation
     */
    resumeAutoRotation() {
      if (this.autoRotate && !this.reducedMotion.matches && this.isInViewport) {
        this.userInteracted = false;
        this.startAutoRotation();
      }
    }

    /**
     * Reset inactivity timer
     */
    resetInactivityTimer() {
      if (this.inactivityTimeout) {
        clearTimeout(this.inactivityTimeout);
      }

      if (this.autoRotate) {
        this.inactivityTimeout = setTimeout(() => {
          this.resumeAutoRotation();
        }, this.inactivityResume);
      }
    }

    /**
     * Handle mouse leave
     */
    handleMouseLeave() {
      if (!this.isDragging) {
        this.resetInactivityTimer();
      }
    }

    /**
     * Handle focus out
     */
    handleFocusOut(e) {
      if (!this.contains(e.relatedTarget)) {
        this.resetInactivityTimer();
      }
    }

    // =========================================================================
    // Viewport Observer
    // =========================================================================

    initViewportObserver() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.isInViewport = entry.isIntersecting;

            if (entry.isIntersecting) {
              if (this.autoRotate && !this.reducedMotion.matches && !this.userInteracted) {
                this.startAutoRotation();
              }
            } else {
              this.pauseAutoRotation();
            }
          });
        },
        { threshold: 0.2 }
      );

      observer.observe(this);
    }

    /**
     * Handle reduced motion preference change
     */
    handleReducedMotionChange() {
      if (this.reducedMotion.matches) {
        this.pauseAutoRotation();
      } else if (this.autoRotate && !this.userInteracted && this.isInViewport) {
        this.startAutoRotation();
      }
    }

    /**
     * Cleanup on disconnect
     */
    disconnectedCallback() {
      this.pauseAutoRotation();

      if (this.inactivityTimeout) {
        clearTimeout(this.inactivityTimeout);
      }

      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);
    }
  }

  customElements.define('brand-wheel-carousel', BrandWheelCarousel);
}
